<template>
  <div class="container-fluid mt-3">
    <h3>Cache Manager</h3>
    <p class="text-muted">Clear cached data for the current tenant. Caches automatically repopulate on next use.</p>

    <div class="mb-4">
      <b-button variant="danger" size="lg" @click="clear_all()" :disabled="any_loading">
        <b-spinner v-if="loading_all" small></b-spinner>
        Clear All Caches
      </b-button>
    </div>

    <hr/>

    <h5>
      Telematics Caches
      <b-button variant="warning" size="sm" class="ml-2" @click="clear_all_telematics()" :disabled="any_loading">
        <b-spinner v-if="loading_all_telematics" small></b-spinner>
        Clear All Telematics
      </b-button>
    </h5>
    <p class="text-muted small">Driver, vehicle, and address lists from the telematics provider ({{telematics_type_display}}). Default TTL: 4 hours.</p>
    <div class="mb-3">
      <b-button v-for="item in telematics_caches" :key="item.type"
                variant="outline-primary" class="mr-2 mb-2"
                @click="clear_cache(item.type, item.api)" :disabled="any_loading || loading[item.type]">
        <b-spinner v-if="loading[item.type]" small></b-spinner>
        {{item.label}}
      </b-button>
    </div>

    <template v-if="is_tbn">
      <hr/>
      <h5>
        TBN Caches
        <b-button variant="warning" size="sm" class="ml-2" @click="clear_all_tbn()" :disabled="any_loading">
          <b-spinner v-if="loading_all_tbn" small></b-spinner>
          Clear All TBN
        </b-button>
      </h5>
      <p class="text-muted small">Driver lists, vehicle lists, types, and other TBN API data. Default TTL: 4 hours (types: 1 day).</p>
      <div class="mb-3">
        <b-button v-for="item in tbn_caches" :key="item.type"
                  variant="outline-info" class="mr-2 mb-2"
                  @click="clear_cache(item.type, item.api)" :disabled="any_loading || loading[item.type]">
          <b-spinner v-if="loading[item.type]" small></b-spinner>
          {{item.label}}
        </b-button>
      </div>
    </template>

    <hr/>
    <div v-if="clear_log.length > 0" class="mt-3">
      <h6>Recent Actions</h6>
      <ul class="list-unstyled small">
        <li v-for="(entry, i) in clear_log" :key="i" :class="entry.success ? 'text-success' : 'text-danger'">
          {{entry.time}} &mdash; {{entry.label}}: {{entry.success ? 'Cleared' : 'Failed'}}
          <span v-if="entry.error" class="text-muted"> ({{entry.error}})</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex"
import {tbn_admin_clear_cache} from '../networking/tbn_apis'
import {admin_clear_cache} from '../networking/apis'

export default {
  name: 'cache_manager',

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    is_tbn() {
      if (!this.user || !this.user.types || !this.user.tenant) return false
      return this.user.types[this.user.tenant] === 'TBN'
    },
    telematics_type_display() {
      if (!this.user || !this.user.telematics_types || !this.user.tenant) return ''
      return this.user.telematics_types[this.user.tenant] || 'SAMSARA'
    },
    telematics_caches() {
      const api = this.is_tbn ? 'tbn' : 'cm'
      return [
        {type: 'telematics_driver_list', label: 'Clear Driver List', api},
        {type: 'telematics_vehicle_list', label: 'Clear Vehicle List', api},
        {type: 'telematics_address_list', label: 'Clear Address List', api},
      ]
    },
    tbn_caches() {
      return [
        {type: 'tbn_driver_list', label: 'Clear TBN Driver List', api: 'tbn'},
        {type: 'tbn_vehicle_list', label: 'Clear TBN Vehicle List', api: 'tbn'},
        {type: 'tbn_vehicle_types', label: 'Clear TBN Vehicle Types', api: 'tbn'},
        {type: 'tbn_driver_types', label: 'Clear TBN Driver Types', api: 'tbn'},
        {type: 'tbn_scheduled_services', label: 'Clear TBN Scheduled Services', api: 'tbn'},
        {type: 'scorecard_driver_list', label: 'Clear Scorecard Driver List', api: 'tbn'},
      ]
    },
    any_loading() {
      return this.loading_all || this.loading_all_telematics || this.loading_all_tbn
    }
  },

  data() {
    return {
      loading: {},
      loading_all: false,
      loading_all_telematics: false,
      loading_all_tbn: false,
      clear_log: [],
    }
  },

  methods: {
    async clear_cache(cache_type, api, silent) {
      if (!this.user || !this.user.tenant) return
      this.$set(this.loading, cache_type, true)
      const label = this._label_for(cache_type)
      try {
        const isTelematics = cache_type.startsWith('telematics_')
        if (api === 'tbn') {
          const promises = [tbn_admin_clear_cache(this.user.tenant, cache_type)]
          // Telematics caches are shared between tbn-integration and fleet-int-api.
          // fleet-int-api holds its own in-memory cache that must also be cleared.
          if (isTelematics) {
            promises.push(admin_clear_cache(this.user.tenant, cache_type))
          }
          await Promise.all(promises)
        } else {
          await admin_clear_cache(this.user.tenant, cache_type)
        }
        this.clear_log.unshift({time: this._now(), label, success: true})
        if (!silent) {
          this.$bvToast.toast(`${label} cleared successfully`, {title: 'Cache Cleared', variant: 'success', autoHideDelay: 2000})
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || 'Unknown error'
        this.clear_log.unshift({time: this._now(), label, success: false, error: msg})
        if (!silent) {
          this.$bvToast.toast(`Failed to clear ${label}: ${msg}`, {title: 'Error', variant: 'danger', autoHideDelay: 4000})
        }
      } finally {
        this.$set(this.loading, cache_type, false)
      }
    },

    async _clear_batch(caches, groupLabel) {
      const results = await Promise.allSettled(caches.map(c => this.clear_cache(c.type, c.api, true)))
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed === 0) {
        this.$bvToast.toast(`${groupLabel}: all caches cleared successfully`, {title: 'Cache Cleared', variant: 'success', autoHideDelay: 2000})
      } else {
        this.$bvToast.toast(`${groupLabel}: ${failed} of ${results.length} failed (see log below)`, {title: 'Error', variant: 'danger', autoHideDelay: 4000})
      }
    },

    async clear_all_telematics() {
      this.loading_all_telematics = true
      try {
        await this._clear_batch(this.telematics_caches, 'Telematics')
      } finally {
        this.loading_all_telematics = false
      }
    },

    async clear_all_tbn() {
      this.loading_all_tbn = true
      try {
        await this._clear_batch(this.tbn_caches, 'TBN')
      } finally {
        this.loading_all_tbn = false
      }
    },

    async clear_all() {
      this.loading_all = true
      try {
        const all = [...this.telematics_caches]
        if (this.is_tbn) {
          all.push(...this.tbn_caches)
        }
        await this._clear_batch(all, 'All Caches')
      } finally {
        this.loading_all = false
      }
    },

    _label_for(cache_type) {
      const all = [...this.telematics_caches, ...this.tbn_caches]
      const found = all.find(c => c.type === cache_type)
      return found ? found.label.replace('Clear ', '') : cache_type
    },

    _now() {
      return new Date().toLocaleTimeString()
    }
  }
}
</script>
